import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { useDispatch } from "react-redux";
import config from "@/config";
import { useGoogleMaps } from "@/utils/googleMaps.jsx";
import { addToast } from "@/store/app/action.js";
import { getMerchants, getStoreById, getManageProductDetails } from "@/api/admin.js";
import { getPlans as getMarketplaceCategories } from "@/api/marketplace.js";
import { getProductTags } from "@/api/productTags.js";
import { getProductGeorestrictions } from "@/api/productGeorestrictions.js";
import {
  buildProductProfile,
  evaluateCompliance,
  storesCoveringAddress,
  parseGooglePlace,
  placeFromStore,
  ruleName,
} from "./complianceCheck.js";

function Step({ n, title, hint }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-white dark:bg-slate-200 dark:text-slate-900">
          {n}
        </span>
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h2>
      </div>
      {hint ? (
        <p className="mt-1 pl-8 text-sm text-slate-500 dark:text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    indigo: "bg-indigo-100 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300",
    rose: "bg-rose-100 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${tones[tone]}`}>
      {children}
    </span>
  );
}

function categoryList(hit) {
  const raw = hit?.default_category || hit?.base_category;
  if (!raw) return "";
  return Array.isArray(raw) ? raw.filter(Boolean).join(", ") : String(raw);
}

function addressKind(rule) {
  if (rule.address_type === "platform") return "Everywhere";
  if (rule.address_type === "store") return "Store address";
  if (rule.address_type === "either") return "Store or delivery address";
  return "Delivery address";
}

function marketplaceCategories(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.categories)
      ? data.categories
      : [];
  return raw
    .map((c) => {
      const id = String(c?.id || c?._id || "").trim();
      const name = String(c?.name || c?.label || "").trim();
      if (!id || !name) return null;
      return { id, name, tag: String(c?.tag || "").trim() };
    })
    .filter(Boolean);
}

function storesFromMerchants(res) {
  const collected = [];
  (res?.merchants || []).forEach((merchant) => {
    (merchant.locations || []).forEach((loc) => {
      if (!loc?.store_id) return;
      collected.push({
        ...loc,
        account: merchant.name,
      });
    });
  });
  return collected;
}

function storeKind(store) {
  if (store?.direct === false || store?.store_type === "marketplace" || store?.type === "marketplace") {
    return "Marketplace";
  }
  if (store?.direct === true || store?.store_type === "direct" || store?.type === "direct") {
    return "Direct";
  }
  return "";
}

async function loadStores() {
  // One request, no storeType, so Direct and Marketplace both come back.
  // Page is ignored by this endpoint and repeats the same payload.
  const res = await getMerchants(1, 100, null, false);
  const seen = new Set();
  return storesFromMerchants(res)
    .filter((store) => {
      if (seen.has(store.store_id)) return false;
      seen.add(store.store_id);
      return true;
    })
    .sort((a, b) => String(a.name || a.store_id).localeCompare(String(b.name || b.store_id)));
}

async function hydrateStore(store) {
  const id = store?.id || store?._id;
  if (!id) return store;
  try {
    const res = await getStoreById(id);
    const full = res?.store || res?.seller || res;
    if (!full || typeof full !== "object") return store;
    return {
      ...store,
      ...full,
      store_id: full.store_id || store.store_id,
      account: store.account,
      storeType: store.storeType,
    };
  } catch {
    return store;
  }
}

function RuleCard({ item, tone }) {
  const border = {
    rose: "border-rose-200 dark:border-rose-500/30",
    amber: "border-amber-200 dark:border-amber-500/30",
    slate: "border-slate-200 dark:border-slate-700",
  }[tone];
  return (
    <article className={`rounded-lg border bg-white dark:bg-theme-surface-accent px-4 py-3 ${border}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          {ruleName(item.rule)}
        </h3>
        <Pill tone={tone === "rose" ? "rose" : tone === "amber" ? "amber" : "slate"}>
          {item.action}
        </Pill>
        <Pill>{addressKind(item.rule)}</Pill>
        {item.rule.active === false ? <Pill>Turned off</Pill> : null}
      </div>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
        {item.because} {item.where}
      </p>
    </article>
  );
}

function ChannelCard({ label, result }) {
  const styles = {
    allowed: "border-emerald-200 bg-emerald-50/70 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    blocked: "border-rose-200 bg-rose-50/70 dark:border-rose-500/30 dark:bg-rose-500/10",
    unknown: "border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10",
  };
  const titles = { allowed: "Allowed", blocked: "Blocked", unknown: "Not sure yet" };
  const titleColor = {
    allowed: "text-emerald-700 dark:text-emerald-300",
    blocked: "text-rose-700 dark:text-rose-300",
    unknown: "text-amber-800 dark:text-amber-300",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${styles[result.status]}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className={`mt-1 text-lg font-semibold ${titleColor[result.status]}`}>
        {titles[result.status]}
      </div>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{result.summary}</p>
    </div>
  );
}

function ComplianceCheck() {
  const dispatch = useDispatch();
  const { isLoaded: mapsLoaded } = useGoogleMaps();
  const addressAutocomplete = useRef(null);
  const productBox = useRef(null);
  const storeBox = useRef(null);
  const searchRequest = useRef(0);
  const polygonCache = useRef(new Map());

  const [searchClient] = useState(() => new TypesenseInstantSearchAdapter({
    server: {
      apiKey: config.ts.key,
      nodes: config.ts.nodes,
      nearestNode: config.ts.nearestNode,
      cacheSearchResultsForSeconds: 0,
    },
    additionalSearchParameters: {
      query_by: "title,subtitle,description,default_category,company",
      group_by: "sku_id",
      group_limit: 1,
    },
  }).searchClient);

  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [refsError, setRefsError] = useState("");
  const [refsLoading, setRefsLoading] = useState(true);

  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState("");

  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const [hit, setHit] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  const [addressText, setAddressText] = useState("");
  const [googlePlace, setGooglePlace] = useState(null);
  const [matchedStore, setMatchedStore] = useState(null);
  const [coveringStores, setCoveringStores] = useState([]);
  const [matchNote, setMatchNote] = useState("");
  const [lookupState, setLookupState] = useState("idle");

  const [storeQuery, setStoreQuery] = useState("");
  const [storeOpen, setStoreOpen] = useState(false);
  const [pickedStore, setPickedStore] = useState(null);
  const [hydratedStore, setHydratedStore] = useState(null);
  const [hydrating, setHydrating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRefsLoading(true);
    Promise.all([
      getProductGeorestrictions(),
      getMarketplaceCategories(),
      getProductTags(),
    ]).then(([ruleData, categoryData, tagData]) => {
      if (cancelled) return;
      setRules(Array.isArray(ruleData) ? ruleData : []);
      setCategories(marketplaceCategories(categoryData));
      setTagOptions(Array.isArray(tagData) ? tagData : []);
      setRefsError("");
    }).catch((error) => {
      if (cancelled) return;
      setRefsError(error.response?.data?.message || "Couldn't load georestriction rules.");
    }).finally(() => {
      if (!cancelled) setRefsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStoresLoading(true);
    loadStores().then((list) => {
      if (cancelled) return;
      setStores(list);
      setStoresError("");
    }).catch((error) => {
      if (cancelled) return;
      setStoresError(error.response?.data?.message || "Couldn't load stores.");
    }).finally(() => {
      if (!cancelled) setStoresLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onClick = (event) => {
      if (productBox.current && !productBox.current.contains(event.target)) setSearchOpen(false);
      if (storeBox.current && !storeBox.current.contains(event.target)) setStoreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || hit) {
      setHits([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }
    const requestId = searchRequest.current + 1;
    searchRequest.current = requestId;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await searchClient.search([{
          indexName: config.ts.index,
          params: { query: q, hitsPerPage: 8 },
        }]);
        if (searchRequest.current !== requestId) return;
        const next = response?.results?.[0]?.hits || [];
        const seen = new Set();
        setHits(next.filter((item) => {
          const sku = item.sku_id || item.objectID;
          if (!sku || seen.has(sku)) return false;
          seen.add(sku);
          return true;
        }));
        setSearchError("");
      } catch (error) {
        if (searchRequest.current !== requestId) return;
        setHits([]);
        setSearchError(error?.message || "Product search failed.");
      } finally {
        if (searchRequest.current === requestId) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, hit, searchClient]);

  useEffect(() => {
    if (!googlePlace) {
      setCoveringStores([]);
      setMatchedStore(null);
      setMatchNote("");
      setLookupState("idle");
      return undefined;
    }
    if (googlePlace.lat == null || googlePlace.lng == null) {
      setCoveringStores([]);
      setMatchedStore(null);
      setMatchNote("");
      setLookupState("none");
      return undefined;
    }
    if (storesLoading) {
      setLookupState("searching");
      return undefined;
    }
    let cancelled = false;
    setLookupState("searching");

    const withPolygons = async () => {
      const known = (store) => polygonCache.current.get(store.store_id) || store;
      const missing = stores.filter((store) => {
        const source = known(store);
        return !source.polygon && !source.delivery_polygon && !source._polygonLoaded && (store.id || store._id);
      });
      if (missing.length) {
        const limit = 6;
        let cursor = 0;
        const worker = async () => {
          while (cursor < missing.length) {
            const index = cursor;
            cursor += 1;
            const loaded = await hydrateStore(missing[index]);
            if (loaded?.store_id) {
              polygonCache.current.set(loaded.store_id, { ...loaded, _polygonLoaded: true });
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(limit, missing.length) }, worker));
      }
      return stores.map((store) => known(store));
    };

    withPolygons().then((ready) => {
      if (cancelled) return;
      const covering = storesCoveringAddress(ready, googlePlace);
      setCoveringStores(covering);
      setMatchedStore((current) => covering.find((store) => store.store_id === current?.store_id) || covering[0] || null);
      setMatchNote(covering.length
        ? `${covering.length} ${covering.length === 1 ? "store delivers" : "stores deliver"} to this address.`
        : "");
      setLookupState(covering.length ? "found" : "none");
    }).catch(() => {
      if (cancelled) return;
      setCoveringStores([]);
      setMatchedStore(null);
      setMatchNote("Couldn't load delivery areas.");
      setLookupState("error");
    });
    return () => {
      cancelled = true;
    };
  }, [googlePlace, stores, storesLoading]);

  useEffect(() => {
    if (!pickedStore) {
      setHydratedStore(null);
      setHydrating(false);
      return undefined;
    }
    let cancelled = false;
    setHydrating(true);
    hydrateStore(pickedStore).then((store) => {
      if (cancelled) return;
      setHydratedStore(store);
      setHydrating(false);
    });
    return () => {
      cancelled = true;
    };
  }, [pickedStore]);

  const selectHit = (next) => {
    setHit(next);
    setQuery([next.title, next.subtitle].filter(Boolean).join(" "));
    setSearchOpen(false);
    setHits([]);
    setDetails(null);
    setDetailsError("");
    const sku = next.sku_id;
    if (!sku) return;
    setDetailsLoading(true);
    getManageProductDetails(sku).then((product) => {
      setDetails(product);
    }).catch((error) => {
      setDetails(null);
      setDetailsError(error.response?.data?.message || "Product details didn't load. Category and tag matching may be incomplete.");
    }).finally(() => setDetailsLoading(false));
  };

  const clearProduct = () => {
    setHit(null);
    setDetails(null);
    setDetailsError("");
    setQuery("");
    setHits([]);
  };

  const onPlaceChanged = () => {
    const place = addressAutocomplete.current?.getPlace();
    const parsed = parseGooglePlace(place);
    if (!parsed) {
      dispatch(addToast({ message: "Choose an address from the suggestions.", type: "error" }));
      return;
    }
    setPickedStore(null);
    setStoreQuery("");
    setAddressText(parsed.formatted || addressText);
    setGooglePlace(parsed);
  };

  const clearAddress = () => {
    setAddressText("");
    setGooglePlace(null);
    setMatchedStore(null);
    setCoveringStores([]);
    setMatchNote("");
    setLookupState("idle");
  };

  const selectStore = (store) => {
    clearAddress();
    setPickedStore(store);
    setStoreQuery("");
    setStoreOpen(false);
  };

  const activeStore = googlePlace ? matchedStore : hydratedStore;
  const locationPending = (googlePlace && lookupState === "searching")
    || (pickedStore && hydrating);

  const storePlace = useMemo(() => {
    if (googlePlace && matchedStore) return placeFromStore(matchedStore);
    if (googlePlace && lookupState === "none") return googlePlace;
    if (!googlePlace && hydratedStore) return placeFromStore(hydratedStore);
    return null;
  }, [googlePlace, matchedStore, lookupState, hydratedStore]);

  const deliveryPlace = googlePlace && (matchedStore || lookupState === "none") ? googlePlace : null;
  const usingAddressAsBoth = Boolean(googlePlace && lookupState === "none");

  const profile = useMemo(() => {
    if (!hit) return null;
    return buildProductProfile({ hit, details, categories, tagOptions });
  }, [hit, details, categories, tagOptions]);

  const result = useMemo(() => {
    if (!profile || refsLoading || detailsLoading || locationPending) return null;
    return evaluateCompliance({
      product: profile,
      rules,
      storePlace,
      deliveryPlace,
      categories,
      tagOptions,
    });
  }, [
    profile,
    refsLoading,
    detailsLoading,
    locationPending,
    rules,
    storePlace,
    deliveryPlace,
    categories,
    tagOptions,
  ]);

  const storeChoices = useMemo(() => {
    const q = storeQuery.trim().toLowerCase();
    const list = q
      ? stores.filter((store) => [store.name, store.store_id, store.city, store.state, store.account]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q))
      : stores;
    return list.slice(0, 40);
  }, [stores, storeQuery]);

  const saleTone = result?.sale.status === "no"
    ? "rose"
    : result?.sale.status === "yes"
      ? "emerald"
      : "amber";
  const saleClass = {
    rose: "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10",
    emerald: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    amber: "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10",
  }[saleTone];
  const saleTitleClass = {
    rose: "text-rose-700 dark:text-rose-300",
    emerald: "text-emerald-700 dark:text-emerald-300",
    amber: "text-amber-800 dark:text-amber-200",
  }[saleTone];

  const locationSummary = (() => {
    if (!googlePlace && !pickedStore) {
      return "No store or address yet. Only rules that apply everywhere can be decided.";
    }
    if (googlePlace && lookupState === "found" && activeStore) {
      const extra = coveringStores.length > 1
        ? ` ${coveringStores.length} stores deliver here. Rules below use ${activeStore.store_id}.`
        : "";
      return `Delivery address is the one you entered. Store rules use ${activeStore.name || activeStore.store_id}.${extra}`;
    }
    if (usingAddressAsBoth) {
      return "No delivery area covers this address. Rules are checked as if this address were both the store and the delivery address.";
    }
    if (pickedStore && hydratedStore) {
      return `Using store ${hydratedStore.store_id}. Delivery-address rules stay undecided without a customer drop-off.`;
    }
    return "Looking up the location…";
  })();

  return (
    <div className="space-y-6">
      <style>{".pac-container{z-index:10000}"}</style>
      <section className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent p-4 sm:p-5">
        <Step
          n={1}
          title="Find the product"
          hint="Searches the master menu with no filters."
        />
        <div ref={productBox} className="relative pl-0 sm:pl-8">
          <label className="block text-sm font-medium mb-1" htmlFor="compliance-product-search">
            Product
          </label>
          <input
            id="compliance-product-search"
            className="form-input w-full"
            placeholder="Search by name, brand, or category"
            value={query}
            autoComplete="off"
            onChange={(e) => {
              setQuery(e.target.value);
              if (hit) {
                setHit(null);
                setDetails(null);
              }
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
          />
          {hit ? (
            <button
              type="button"
              className="absolute right-2 top-[34px] text-xs font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              onClick={clearProduct}
            >
              Clear
            </button>
          ) : null}
          {searchOpen && !hit && query.trim().length >= 2 ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
              {searching ? (
                <p className="px-3 py-3 text-sm text-slate-500">Searching the master menu…</p>
              ) : searchError ? (
                <p className="px-3 py-3 text-sm text-rose-600">{searchError}</p>
              ) : hits.length === 0 ? (
                <p className="px-3 py-3 text-sm text-slate-500">No products found.</p>
              ) : hits.map((item) => (
                <button
                  key={item.sku_id || item.objectID}
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/60"
                  onClick={() => selectHit(item)}
                >
                  {item.image ? (
                    <img src={item.image} alt="" className="h-10 w-10 rounded object-cover bg-slate-100" />
                  ) : (
                    <span className="h-10 w-10 rounded bg-slate-100 dark:bg-slate-700" />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                      {[item.title, item.subtitle].filter(Boolean).join(" ")}
                    </span>
                    <span className="block truncate text-xs text-slate-500">
                      {[item.company, categoryList(item), item.sku_id].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {hit ? (
            <div className="mt-3 flex flex-wrap items-start gap-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-3">
              {profile?.image ? (
                <img src={profile.image} alt="" className="h-14 w-14 rounded object-cover bg-white" />
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {[profile?.title, profile?.subtitle].filter(Boolean).join(" ")}
                  {profile?.volume ? <span className="font-normal text-slate-500"> · {profile.volume}</span> : null}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {[profile?.company, profile?.sku && `SKU ${profile.sku}`, profile?.upc && `UPC ${profile.upc}`].filter(Boolean).join(" · ")}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(profile?.categoryNames || []).map((name) => (
                    <Pill key={name} tone="indigo">{name}</Pill>
                  ))}
                  {(profile?.tagLabels || []).map((label) => (
                    <Pill key={label}>{label}</Pill>
                  ))}
                  {profile && !profile.categoryNames.length && !profile.tagLabels.length ? (
                    <span className="text-xs text-slate-500">No category or tag on this product.</span>
                  ) : null}
                </div>
                {detailsLoading ? (
                  <p className="mt-2 text-xs text-slate-500">Loading categories and tags…</p>
                ) : null}
                {detailsError ? (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{detailsError}</p>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">Type at least 2 characters, then pick a product.</p>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent p-4 sm:p-5">
        <Step
          n={2}
          title="Where should we check?"
          hint="Optional. Enter an address to see every store whose delivery area covers it, or pick a store directly."
        />
        <div className="grid grid-cols-1 items-start gap-4 pl-0 sm:pl-8 lg:grid-cols-[1fr_auto_1fr]">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="compliance-address">
              Address
            </label>
            {mapsLoaded ? (
              <Autocomplete
                onLoad={(autocomplete) => {
                  addressAutocomplete.current = autocomplete;
                }}
                onPlaceChanged={onPlaceChanged}
                options={{
                  types: ["address"],
                  componentRestrictions: { country: "us" },
                  fields: ["address_components", "geometry", "formatted_address"],
                }}
              >
                <input
                  id="compliance-address"
                  className="form-input w-full"
                  placeholder="Start typing a street address"
                  value={addressText}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAddressText(next);
                    if (googlePlace && next.trim() !== String(googlePlace.formatted || "").trim()) {
                      setGooglePlace(null);
                    }
                  }}
                />
              </Autocomplete>
            ) : (
              <input className="form-input w-full" placeholder="Loading address search…" disabled />
            )}
            {lookupState === "searching" ? (
              <p className="mt-1 text-xs text-slate-500">Checking which delivery areas cover this address…</p>
            ) : null}
          </div>

          <div className="flex items-center justify-center text-xs font-semibold uppercase tracking-wide text-slate-400 lg:pt-8">
            or
          </div>

          <div ref={storeBox} className="relative">
            <label className="block text-sm font-medium mb-1" htmlFor="compliance-store">
              Store ID
            </label>
            <input
              id="compliance-store"
              className="form-input w-full"
              placeholder={storesLoading ? "Loading stores…" : "Search by store name or ID"}
              value={storeOpen ? storeQuery : (pickedStore ? `${pickedStore.store_id} — ${pickedStore.name || ""}` : storeQuery)}
              disabled={storesLoading || Boolean(storesError)}
              autoComplete="off"
              onChange={(e) => {
                setStoreQuery(e.target.value);
                setStoreOpen(true);
                if (pickedStore) setPickedStore(null);
              }}
              onFocus={() => {
                setStoreOpen(true);
                setStoreQuery("");
              }}
            />
            {pickedStore ? (
              <button
                type="button"
                className="absolute right-2 top-[34px] text-xs font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                onClick={() => setPickedStore(null)}
              >
                Clear
              </button>
            ) : null}
            {storeOpen && !storesLoading ? (
              <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
                {storesError ? (
                  <p className="px-3 py-3 text-sm text-rose-600">{storesError}</p>
                ) : storeChoices.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-slate-500">No stores match.</p>
                ) : storeChoices.map((store) => (
                  <button
                    key={store.store_id}
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/60"
                    onClick={() => selectStore(store)}
                  >
                    <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                      <span className="font-mono">{store.store_id}</span>
                      {store.name ? ` — ${store.name}` : ""}
                    </span>
                    <span className="block text-xs text-slate-500">
                      {[store.city, store.state, storeKind(store)].filter(Boolean).join(" · ")}
                    </span>
                  </button>
                ))}
                {stores.length > storeChoices.length ? (
                  <p className="px-3 py-2 text-xs text-slate-400">Keep typing to narrow the list.</p>
                ) : null}
              </div>
            ) : null}
            {pickedStore && placeFromStore(hydratedStore || pickedStore)?.formatted ? (
              <p className="mt-2 text-xs text-slate-500">
                {placeFromStore(hydratedStore || pickedStore).formatted}
                {hydrating ? " · loading the full store address…" : ""}
              </p>
            ) : null}
            {storesError ? <p className="mt-2 text-xs text-rose-600">{storesError}</p> : null}
          </div>
        </div>

        {lookupState === "found" && coveringStores.length > 0 ? (
          <div className="mt-4 sm:pl-8">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                {matchNote}
              </div>
              <button type="button" className="shrink-0 text-xs font-medium text-indigo-600 dark:text-indigo-300" onClick={clearAddress}>
                Clear address
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {coveringStores.map((store) => {
                const selected = store.store_id === activeStore?.store_id;
                const formatted = placeFromStore(store)?.formatted;
                const meta = [storeKind(store), formatted].filter(Boolean).join(" · ");
                return (
                  <button
                    key={store.store_id}
                    type="button"
                    onClick={() => setMatchedStore(store)}
                    className={`rounded-md border px-2.5 py-1.5 text-left ${
                      selected
                        ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500 dark:border-indigo-400 dark:bg-indigo-500/10 dark:ring-indigo-400"
                        : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-600 dark:bg-slate-800/40"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="min-w-0 truncate text-sm text-slate-800 dark:text-slate-100">
                        <span className="font-mono font-semibold">{store.store_id}</span>
                        {store.name ? <span className="font-normal text-slate-600 dark:text-slate-300"> {store.name}</span> : null}
                      </span>
                      {selected ? (
                        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">
                          Checking
                        </span>
                      ) : null}
                    </div>
                    {meta ? (
                      <div className="truncate text-xs text-slate-500">{meta}</div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        {lookupState === "none" || lookupState === "error" ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 sm:ml-8">
            {matchNote || "No store’s delivery area covers this address. The check still runs against the address itself."}
            <button type="button" className="ml-2 text-xs font-medium underline" onClick={clearAddress}>
              Clear address
            </button>
          </div>
        ) : null}
      </section>

      <section>
        <Step n={3} title="What the rules say" />
        {!hit ? (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 px-6 py-10 text-center">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Pick a product to run the check.</p>
            <p className="mt-1 text-sm text-slate-500">Location is optional. Without it, only platform-wide rules can be decided.</p>
          </div>
        ) : refsError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
            {refsError}
          </div>
        ) : !result ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
            Checking rules…
          </div>
        ) : (
          <div className="space-y-4 pl-0 sm:pl-8">
            {profile?.disabled ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                This product is turned off in the master menu, so customers can’t buy it anywhere until it’s turned back on.
                The result below is only about georestriction rules.
              </div>
            ) : null}

            <div className={`rounded-lg border px-4 py-4 ${saleClass}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Can this product be sold?
              </div>
              <div className={`mt-1 text-2xl font-semibold ${saleTitleClass}`}>{result.sale.title}</div>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{result.sale.detail}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{locationSummary}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChannelCard label="Pickup" result={result.pickup} />
              <ChannelCard label="Delivery" result={result.delivery} />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Rules that applied ({result.applied.length})
              </h3>
              <p className="mt-1 mb-3 text-sm text-slate-500 dark:text-slate-400">
                These match the product and the location, and they are turned on.
              </p>
              {result.applied.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 px-4 py-6 text-sm text-slate-500">
                  No active rule applies to this product here.
                </div>
              ) : (
                <div className="space-y-2">
                  {result.applied.map((item) => (
                    <RuleCard
                      key={item.rule.id || ruleName(item.rule)}
                      item={item}
                      tone={item.rule.restrict === "both" ? "rose" : "amber"}
                    />
                  ))}
                </div>
              )}
            </div>

            {result.unchecked.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Couldn’t fully check ({result.unchecked.length})
                </h3>
                <p className="mt-1 mb-3 text-sm text-slate-500 dark:text-slate-400">
                  These rules target the product, but the address we have isn’t enough to know if they apply.
                </p>
                <div className="space-y-2">
                  {result.unchecked.map((item) => (
                    <RuleCard key={item.rule.id || ruleName(item.rule)} item={item} tone="amber" />
                  ))}
                </div>
              </div>
            ) : null}

            {result.inactive.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Turned off, but would match ({result.inactive.length})
                </h3>
                <p className="mt-1 mb-3 text-sm text-slate-500 dark:text-slate-400">
                  These rules fit the product and the location. They are not enforced.
                </p>
                <div className="space-y-2">
                  {result.inactive.map((item) => (
                    <RuleCard key={item.rule.id || ruleName(item.rule)} item={item} tone="slate" />
                  ))}
                </div>
              </div>
            ) : null}

            {result.notHere.length > 0 ? (
              <details className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Targets this product, but not this location ({result.notHere.length})
                </summary>
                <div className="mt-3 space-y-2">
                  {result.notHere.map((item) => (
                    <RuleCard key={item.rule.id || ruleName(item.rule)} item={item} tone="slate" />
                  ))}
                </div>
              </details>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}

export default ComplianceCheck;
